import { ChevronRight, MapPin } from "lucide-react";
import { data, Link } from "react-router";
import { BreadcrumbJsonLd } from "~/components/seo/breadcrumb-jsonld";
import { envConfig } from "~/lib/env-validation";
import { generateMetaTags, SITE_URL } from "~/lib/seo";
import type { Route } from "./+types/kraj";

// --- Types ---

interface RegionItem {
	slug: string;
	name: string;
	companyCount: number;
}

interface RegionsResponse {
	regions: RegionItem[];
}

// --- Loader ---

export async function loader() {
	const res = await fetch(`${envConfig.apiBaseUrl}/directory/regions`);

	if (!res.ok) {
		throw data(null, { status: 500 });
	}

	const result: RegionsResponse = await res.json();
	return { regions: result.regions };
}

// --- Meta ---

export function meta() {
	return generateMetaTags({
		title: "Firmy podle krajů | FirmIndex",
		description:
			"Přehled českých firem podle krajů. Procházejte firmy v jednotlivých regionech České republiky.",
		canonical: `${SITE_URL}/directory/kraj`,
	});
}

// --- Component ---

function formatCount(count: number): string {
	if (count >= 1000) {
		return `${Math.round(count / 1000)} tis.`;
	}
	return count.toLocaleString("cs-CZ");
}

export default function KrajPage({ loaderData }: Route.ComponentProps) {
	const { regions } = loaderData;

	const totalCompanies = regions.reduce((sum, r) => sum + r.companyCount, 0);

	return (
		<>
			<BreadcrumbJsonLd
				items={[
					{ name: "FirmIndex", url: "/" },
					{ name: "Kraje", url: "/directory/kraj" },
				]}
			/>

			<div>
				{/* Header */}
				<div className="px-4 pt-6">
					<div className="public-hero mx-auto max-w-5xl rounded-3xl px-6 py-10 sm:px-8">
						<p className="label-overline mb-2">Firemní katalog</p>
						<h1 className="text-2xl font-bold sm:text-3xl">Firmy podle krajů</h1>
						<p className="mt-2 text-muted-foreground">
							Procházejte firmy v jednotlivých regionech České republiky. Celkem{" "}
							<strong>{totalCompanies.toLocaleString("cs-CZ")}</strong> firem ve{" "}
							<strong>{regions.length}</strong> krajích.
						</p>
					</div>
				</div>

				{/* Content */}
				<main className="mx-auto max-w-5xl px-4 py-6">
					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
						{regions.map((region) => (
							<Link
								key={region.slug}
								to={`/directory/kraj/${region.slug}`}
								className="card-surface flex items-center justify-between gap-3 rounded-2xl p-4 transition-all hover:-translate-y-0.5 hover:shadow-md"
							>
								<div className="flex items-start gap-3 min-w-0">
									<span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-primary/10 text-primary">
										<MapPin className="h-4 w-4" />
									</span>
									<div className="min-w-0">
										<h2 className="text-sm font-semibold leading-tight">{region.name}</h2>
										<p className="mt-0.5 text-xs text-muted-foreground">
											{formatCount(region.companyCount)}{" "}
											{region.companyCount === 1
												? "firma"
												: region.companyCount < 5
													? "firmy"
													: "firem"}
										</p>
									</div>
								</div>
								<ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
							</Link>
						))}
					</div>

					{regions.length === 0 && (
						<div className="flex flex-col items-center gap-2 py-16 text-center">
							<MapPin className="h-12 w-12 text-muted-foreground" />
							<p className="text-sm text-muted-foreground">
								Zatím nejsou k dispozici žádné regiony.
							</p>
						</div>
					)}
				</main>
			</div>
		</>
	);
}
